import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarHeart, Loader2, Send, Eye } from "lucide-react";
import { toast } from "sonner";
import { fetchWeeklyDigestSettings, saveWeeklyDigestSettings, fetchWeeklyDigestPreview, sendWeeklyDigestNow } from "../lib/api";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

// Owner: Sunday summary for every family member with an email — filed, due soon, guestbook signatures.
export const WeeklyDigestRow = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["weekly-digest-settings"], queryFn: fetchWeeklyDigestSettings });
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);

  const save = async (payload, ok) => {
    setBusy("save");
    try {
      qc.setQueryData(["weekly-digest-settings"], await saveWeeklyDigestSettings(payload));
      toast.success(ok);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
      qc.invalidateQueries({ queryKey: ["weekly-digest-settings"] });
    } finally { setBusy(""); }
  };
  const sendNow = async () => {
    setBusy("send");
    try {
      const r = await sendWeeklyDigestNow();
      toast.success(`Weekly digest sent to ${r.sent_to.join(", ")}${r.skipped ? ` (${r.skipped} rejected)` : ""}`);
      qc.invalidateQueries({ queryKey: ["weekly-digest-settings"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setBusy(""); }
  };
  const showPreview = async () => {
    setBusy("preview");
    try { setPreview(await fetchWeeklyDigestPreview()); } catch (e) { toast.error(e?.response?.data?.detail || "Could not build preview"); } finally { setBusy(""); }
  };

  if (!data) return null;
  const who = data.recipients.map((r) => r.name).join(", ");
  return (
    <div className="mt-6" data-testid="weekly-digest-section">
      <h4 className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-1.5 mb-2">
        <CalendarHeart className="h-3.5 w-3.5" /> Family weekly digest
      </h4>
      <div className="rounded-lg border border-border bg-card px-3 py-2.5 text-sm space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium">Every Sunday: what was filed, what's due, who signed the guestbook</div>
            <div className="text-xs text-muted-foreground" data-testid="weekly-digest-status">
              {data.enabled ? `On · next ${data.next_send}` : "Off"}{data.last_sent_at ? ` · last sent ${new Date(data.last_sent_at).toLocaleDateString()}` : ""}
            </div>
          </div>
          <Switch data-testid="weekly-digest-toggle" checked={data.enabled} disabled={busy === "save"} onCheckedChange={(v) => save({ enabled: v }, v ? "Family weekly digest turned on" : "Family weekly digest turned off")} />
        </div>
        <div className="text-xs" data-testid="weekly-digest-recipients">
          {data.recipients.length ? <span>Goes to <b>{who}</b></span> : <span className="text-muted-foreground">No member has an email yet — add one next to each name in Family members above.</span>}
        </div>
        <label className="flex items-center justify-between text-xs gap-3">
          <span className="text-muted-foreground">Include executors (read-only members)</span>
          <Switch data-testid="weekly-digest-executor-toggle" checked={data.include_executor} disabled={busy === "save"} onCheckedChange={(v) => save({ include_executor: v }, v ? "Executors included" : "Executors left out")} />
        </label>
        <label className="flex items-center justify-between text-xs gap-3">
          <span className="text-muted-foreground">Add a "one thing to do" this week (the top open check-up item)</span>
          <Switch data-testid="weekly-digest-one-thing-toggle" checked={data.include_one_thing} disabled={busy === "save"} onCheckedChange={(v) => save({ include_one_thing: v }, v ? "We'll add one thing to do" : "One thing to do left out")} />
        </label>
        <div className="flex gap-2 pt-1">
          <Button data-testid="weekly-digest-preview-btn" size="sm" variant="outline" className="h-8 gap-1.5" onClick={showPreview} disabled={!!busy}>{busy === "preview" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Preview</Button>
          <Button data-testid="weekly-digest-send-btn" size="sm" variant="outline" className="h-8 gap-1.5" onClick={sendNow} disabled={!!busy || !data.recipients.length}>{busy === "send" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send now</Button>
        </div>
      </div>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="weekly-digest-preview-dialog">
          <DialogHeader><DialogTitle className="font-serif">Family weekly · {preview?.data.week_label}</DialogTitle></DialogHeader>
          {preview && <div className="rounded-xl overflow-hidden border border-border" dangerouslySetInnerHTML={{ __html: preview.html }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
